import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { BASE_URL } from "../../services/api";
import { hasPermission } from "../../utils/permissions";
import "./Sidebar.css";

const DEFAULT_EXPANDED_MENUS = {
  people: false,
  attendance: false,
  payroll: false,
  leave: false,
  settings: false,
};

function Sidebar({ collapsed, onToggle }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [expandedMenus, setExpandedMenus] = useState(DEFAULT_EXPANDED_MENUS);
  const companyName =
    user?.company_details?.company_name ||
    user?.companyDetails?.company_name ||
    user?.company_name ||
    user?.onboarding?.company_name ||
    "Cedur";
  const activePlanName =
    user?.company_details?.active_plan?.plan_details?.plan_name ||
    user?.company_details?.active_plan?.plan_name ||
    user?.companyDetails?.activePlan?.planDetails?.plan_name ||
    user?.companyDetails?.activePlan?.plan_name ||
    user?.companyDetails?.active_plan?.plan_name ||
    user?.active_plan?.plan_name ||
    user?.onboarding?.selected_plan?.plan_name ||
    "Plan pending";
  const companyLogo =
    user?.profile_photo_path ? `${BASE_URL}/upload/User/${user.profile_photo_path}` : "";
  const companyInitial = (companyName || "C").trim().charAt(0).toUpperCase();
  const footerTitle = `${companyName} | ${activePlanName}`;
  const designationName = user?.designation_name || user?.role_label || "";

  const toggleMenu = (menuKey) => {
    setExpandedMenus((prev) => {
      const isCurrentlyOpen = prev[menuKey];
      const allClosed = { ...DEFAULT_EXPANDED_MENUS };

      return {
        ...allClosed,
        [menuKey]: !isCurrentlyOpen,
      };
    });
  };

  const menuItems = useMemo(() => {
    const items = [
      {
        id: "dashboard",
        icon: "fa-home",
        label: "Dashboard",
        path: "/dashboard",
        type: "single",
      },
    ];

    if (designationName === "Super Admin") {
      items.push({
        id: "plan_pricing",
        icon: "fa-tags",
        label: "Plan Pricing",
        path: "/super-admin/plan-pricing",
        type: "single",
      });
    }

    const peopleSubItems = [
      ...(hasPermission(user, "people.view")
        ? [{ label: "People List", path: "/people/list" }]
        : []),
      ...(hasPermission(user, "salary.view")
        ? [{ label: "Salary", path: "/salary" }]
        : []),
      ...(hasPermission(user, "tax_declarations.view")
        ? [{ label: "Tax Declarations", path: "/deductions" }]
        : []),
      ...(hasPermission(user, "documents.view") || hasPermission(user, "profile.view")
        ? [{ label: "Documents", path: "/people/documents" }]
        : []),
      ...(hasPermission(user, "benefits.view")
        ? [{ label: "Allowance", path: "/allowance-settings" }]
        : []),
    ];

    if (peopleSubItems.length > 0) {
      items.push({
        id: "people",
        icon: "fa-user",
        label: "People",
        type: "expandable",
        subItems: peopleSubItems,
      });
    }

    if ((hasPermission(user, "documents.view") || hasPermission(user, "profile.view")) && !hasPermission(user, "people.view")) {
      items.push({
        id: "documents",
        icon: "fa-folder-open",
        label: "Documents",
        path: "/people/documents",
        type: "single",
      });
    }

    const attendanceSubItems = [
      ...(hasPermission(user, "attendance.mark")
        ? [{ label: "Mark Attendance", path: "/attendance/mark" }]
        : []),
      ...(hasPermission(user, "attendance.view")
        ? [{ label: "View Attendance", path: "/attendance/view" }]
        : []),
    ];

    if (attendanceSubItems.length > 0) {
      items.push({
        id: "attendance",
        icon: "fa-calendar-check",
        label: "Attendance",
        type: "expandable",
        subItems: attendanceSubItems,
      });
    }

    const payrollSubItems = [
      ...(hasPermission(user, "payroll.generate")
        ? [{ label: "Generate Payroll", path: "/payroll/generate" }]
        : []),
      ...(hasPermission(user, "payroll.view")
        ? [{ label: "View Payroll", path: "/payroll/list" }]
        : []),
    ];

    if (payrollSubItems.length > 0) {
      items.push({
        id: "payroll",
        icon: "fa-credit-card",
        label: "Payroll",
        type: "expandable",
        subItems: payrollSubItems,
      });
    }

    const leaveSubItems = [
      ...(hasPermission(user, "leave.types.manage")
        ? [{ label: "Paid Leaves", path: "/leave/master" }]
        : []),
      ...(hasPermission(user, "weekly_holidays.manage")
        ? [{ label: "Weekly Holidays", path: "/holidays/weekly" }]
        : []),
      ...(hasPermission(user, "ad_hoc_holidays.manage")
        ? [{ label: "Ad Hoc Holidays", path: "/holidays/ad-hoc" }]
        : []),
      ...(hasPermission(user, "leave.applications.view")
        ? [{ label: "Leave Applications", path: "/leave/applications" }]
        : []),
      ...(hasPermission(user, "leave.balance.view")
        ? [{ label: "Leave Balance", path: "/leave/balance" }]
        : []),
    ];

    if (leaveSubItems.length > 0) {
      items.push({
        id: "leave",
        icon: "fa-calendar-alt",
        label: "Leaves & Holidays",
        type: "expandable",
        subItems: leaveSubItems,
      });
    }

    if (hasPermission(user, "salary_advances.view")) {
      items.push({
        id: "salary_advances",
        icon: "fa-money-bill-wave",
        label: "Salary Advances",
        path: "/salary/advances",
        type: "single",
      });
    }

    const settingsSubItems = [
      ...(hasPermission(user, "change_password.view")
        ? [{ label: "Change Password", path: "/change-password" }]
        : []),
      ...(hasPermission(user, "attendance.settings.manage")
        ? [{ label: "Attendance Settings", path: "/attendance/settings" }]
        : []),
      ...(hasPermission(user, "salary_advance_settings.manage")
        ? [{ label: "Salary Advance Settings", path: "/salary/advance-settings" }]
        : []),
      ...(hasPermission(user, "roles_permissions.manage")
        ? [{ label: "Roles & Permissions", path: "/roles-permissions" }]
        : []),
    ];

    if (settingsSubItems.length > 0) {
      items.push({
        id: "settings",
        icon: "fa-cog",
        label: "Settings",
        type: "expandable",
        subItems: settingsSubItems,
      });
    }

    if (hasPermission(user, "tickets.view")) {
      items.push({
        id: "tickets",
        icon: "fa-ticket-alt",
        label: "Tickets",
        path: "/tickets",
        type: "single",
      });
    }

    if (hasPermission(user, "archives.view")) {
      items.push({
        id: "archives",
        icon: "fa-archive",
        label: "Archives",
        path: "/archived-employees",
        type: "single",
      });
    }

    return items;
  }, [user, designationName]);

  useEffect(() => {
    const allClosed = { ...DEFAULT_EXPANDED_MENUS };

    let activeMenuKey = null;

    menuItems.forEach((item) => {
      if (item.type === "expandable" && item.subItems) {
        const isChildActive = item.subItems.some(
          (sub) => location.pathname === sub.path,
        );
        if (isChildActive) {
          activeMenuKey = item.id;
        }
      }
    });

    if (activeMenuKey) {
      setExpandedMenus({
        ...allClosed,
        [activeMenuKey]: true,
      });
    } else {
      setExpandedMenus(allClosed);
    }
  }, [location.pathname, menuItems]);

  const isActive = (path) => location.pathname === path;

  const isParentActive = (subItems) => {
    if (!subItems) return false;
    return subItems.some((item) => location.pathname === item.path);
  };

  const handleMenuClick = (item) => {
    if (item.type === "expandable") {
      toggleMenu(item.id);
    } else {
      navigate(item.path);
    }
  };

  const handleSubItemClick = (path) => {
    navigate(path);
  };

  return (
    <div className={`sidebar ${collapsed ? "collapsed" : ""}`}>
      <div className="sidebar-logo">
        {!collapsed ? (
          <>
            <div className="main-logo">
              <img
                src="https://cedurnow.com/assets/img/logos/new-logo.png"
                alt="Logo"
                className="logo-full"
              />
            </div>
            <div className="while-logo">
              <img
                src="https://cedurnow.com/assets/img/logos/new-logo-white.png"
                alt="Logo"
                className="logo-full"
              />
            </div>
          </>
        ) : (
          <div className="main-logo">
            <img
              src="https://cedurnow.com/assets/img/logos/new-logo.png"
              alt="Logo"
              className="logo-small"
            />
          </div>
        )}
        <button className="sidebar-toggle" onClick={onToggle} type="button">
          <i className={`fas ${collapsed ? "fa-angle-right" : "fa-angle-left"}`}></i>
        </button>
      </div>

      <nav className="sidebar-menu">
        <ul className="sidebar-list">
          {menuItems.map((item) => (
            <li key={item.id} className="sidebar-list-item">
              <div
                className={`menu-item ${
                  item.type === "expandable"
                    ? isParentActive(item.subItems)
                      ? "active"
                      : ""
                    : isActive(item.path)
                      ? "active"
                      : ""
                }`}
                onClick={() => handleMenuClick(item)}
              >
                <i className={`fas ${item.icon} menu-icon`}></i>
                {!collapsed && (
                  <>
                    <span className="menu-label">{item.label}</span>
                    {item.type === "expandable" && (
                      <i
                        className={`fas fa-plus expand-icon ${
                          expandedMenus[item.id] ? "rotated" : ""
                        }`}
                      ></i>
                    )}
                  </>
                )}
              </div>

              {!collapsed &&
                item.type === "expandable" &&
                expandedMenus[item.id] && (
                  <ul className="submenu">
                    {item.subItems.map((subItem) => (
                      <li key={subItem.path} className="submenu-list-item">
                        <div
                          className={`submenu-item ${
                            isActive(subItem.path) ? "active" : ""
                          }`}
                          onClick={() => handleSubItemClick(subItem.path)}
                        >
                          <span>{subItem.label}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
            </li>
          ))}
        </ul>
      </nav>

      <div
        className={`sidebar-company-footer ${collapsed ? "collapsed" : ""}`}
        onClick={() => navigate("/profile")}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            navigate("/profile");
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={`Open company profile for ${footerTitle}`}
        title={footerTitle}
      >
        <div className="sidebar-company-avatar">
          {companyLogo ? (
            <img
              src={companyLogo}
              alt={companyName}
              className="sidebar-company-avatar-image"
            />
          ) : (
            <span>{companyInitial}</span>
          )}
        </div>
        {!collapsed && (
          <div className="sidebar-company-meta">
            <span className="sidebar-company-kicker">Company profile</span>
            <span className="sidebar-company-name">{companyName}</span>
            <div className="sidebar-company-footer-row">
              <span className="sidebar-company-plan">{activePlanName}</span>
              <span className="sidebar-company-arrow">
                <i className="fas fa-arrow-up-right-from-square"></i>
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Sidebar;
